<template>
  <main-template>
    <v-row>
      <v-col>
        <span class="text-h4">Pré-Matrículas</span>
      </v-col>
    </v-row>
    <e-filtros-de-pre-matriculas v-model="filtros" class="mb-4" />
    <v-data-table
      :headers="cabecalhos"
      :items="preMatriculas"
      :loading="carregando"
      :options.sync="opcoes"
      :server-items-length="total"
      :footer-props="{ 'items-per-page-options': [15, 30, 50] }"
      no-data-text="Nenhuma pré-matrícula encontrada."
      class="elevation-1"
    >
      <template v-slot:item.nome="{ item }">
        {{ item.nome || (item.aluno && item.aluno.nomecompleto) || "- - -" }}
      </template>
      <template v-slot:item.responsavel="{ item }">{{ item.responsavel_nome || "- - -" }}</template>
      <template v-slot:item.turma="{ item }">
        {{ item.turma ? item.turma.descricao : "- - -" }}
        <v-chip v-if="item.turma" x-small label class="ml-1" title="Pré-matrículas na turma">
          {{ item.turma.quantidade_de_pre_matriculas }} pré-matrícula(s)
        </v-chip>
      </template>
      <template v-slot:item.serie="{ item }">
        {{ item.serie ? item.serie.descricao : "- - -" }}
      </template>
      <template v-slot:item.valor="{ item }">
        {{ item.parcela ? item.parcela.valor : 0 | currency }}
      </template>
      <template v-slot:item.valorpago="{ item }">
        {{ item.parcela ? item.parcela.valorpago : 0 | currency }}
      </template>
      <template v-slot:item.vencimento="{ item }">
        {{
          item.parcela && item.parcela.referencia ? formatarData(item.parcela.referencia) : "- - -"
        }}
      </template>
      <template v-slot:item.criada="{ item }">
        {{ new Date(item.created_at).toLocaleDateString("pt-BR") }}
      </template>
      <template v-slot:item.status="{ item }">
        <v-chip small label dark :color="status(item).cor">{{ status(item).texto }}</v-chip>
      </template>
      <template v-slot:item.acoes="{ item }">
        <!-- Todos os botões ficam sempre visíveis e desabilitados quando não se aplicam; o tooltip
             diz o motivo. O span segura o tooltip, porque botão desabilitado não dispara eventos. -->
        <v-tooltip v-for="acao in acoes(item)" :key="acao.icone" bottom>
          <template v-slot:activator="{ on, attrs }">
            <span v-bind="attrs" v-on="on">
              <v-btn
                x-small
                class="ml-1"
                :color="acao.cor"
                :disabled="!acao.habilitada"
                :loading="acao.carregando"
                @click="acao.executar()"
              >
                <v-icon small>{{ acao.icone }}</v-icon>
              </v-btn>
            </span>
          </template>
          <span>{{ acao.habilitada ? acao.titulo : acao.motivo }}</span>
        </v-tooltip>
      </template>
    </v-data-table>

    <e-modal-registrar-pagamento
      v-if="preMatriculaEmPagamento"
      :pre-matricula-id="preMatriculaEmPagamento"
      @registrado="aoRegistrarPagamento"
      @fechar="preMatriculaEmPagamento = null"
    />

    <e-modal-historico-de-pagamentos
      v-if="preMatriculaNoHistorico"
      :pre-matricula="preMatriculaNoHistorico"
      @fechar="preMatriculaNoHistorico = null"
    />

    <e-modal-efetivar-pre-matricula
      v-if="preMatriculaEmEfetivacao"
      :pre-matricula-id="preMatriculaEmEfetivacao"
      @efetivada="aoEfetivar"
      @fechar="preMatriculaEmEfetivacao = null"
    />

    <e-modal-gerar-mensalidades
      v-if="matriculaEfetivada"
      :matricula="matriculaEfetivada"
      @fechar="matriculaEfetivada = null"
    />
  </main-template>
</template>

<script>
import EFiltrosDePreMatriculas from "@/components/PreMatriculas/EFiltrosDePreMatriculas.vue";
import EModalEfetivarPreMatricula from "@/components/PreMatriculas/EModalEfetivarPreMatricula.vue";
import EModalGerarMensalidades from "@/components/PreMatriculas/EModalGerarMensalidades.vue";
import EModalHistoricoDePagamentos from "@/components/PreMatriculas/EModalHistoricoDePagamentos.vue";
import EModalRegistrarPagamento from "@/components/PreMatriculas/EModalRegistrarPagamento.vue";
import {
  STATUS_PRE_MATRICULA,
  formatarCpf,
  formatarData,
  mensagemDeErro,
} from "@/components/PreMatriculas/helpers";

export default {
  name: "PreMatriculasPage",
  components: {
    EFiltrosDePreMatriculas,
    EModalEfetivarPreMatricula,
    EModalGerarMensalidades,
    EModalHistoricoDePagamentos,
    EModalRegistrarPagamento,
  },
  data() {
    return {
      preMatriculas: [],
      total: 0,
      carregando: false,
      filtros: { busca: "", periodo_id: null, status: null },
      opcoes: { page: 1, itemsPerPage: 15 },
      preMatriculaEmEfetivacao: null,
      preMatriculaEmPagamento: null,
      preMatriculaNoHistorico: null,
      matriculaEfetivada: null,
      baixandoBoleto: null,
      gerandoBoleto: null,
      cabecalhos: [
        { text: "#", value: "id", sortable: false },
        { text: "Aluno", value: "nome", sortable: false },
        { text: "Responsável", value: "responsavel", sortable: false },
        { text: "Turma", value: "turma", sortable: false },
        { text: "Série", value: "serie", sortable: false },
        { text: "Valor", value: "valor", sortable: false },
        { text: "Pago", value: "valorpago", sortable: false },
        { text: "Vencimento", value: "vencimento", sortable: false },
        { text: "Solicitada em", value: "criada", sortable: false },
        { text: "Status", value: "status", sortable: false },
        { text: "Ações", value: "acoes", sortable: false, align: "center" },
      ],
    };
  },
  computed: {
    podeEfetivar() {
      return this.$can(["preMatriculas.efetivar", "preMatriculas.*"]);
    },
    podeRegistrarPagamento() {
      return this.$can(["financeiro.baixarParcela", "financeiro.*"]);
    },
  },
  watch: {
    opcoes: { handler: "listar", deep: true },
    filtros: {
      handler() {
        // Filtro novo volta para a primeira página, que dispara a listagem pelo watcher acima.
        if (this.opcoes.page !== 1) this.opcoes = { ...this.opcoes, page: 1 };
        else this.listar();
      },
      deep: true,
    },
  },
  methods: {
    formatarData,
    formatarCpf,
    status(preMatricula) {
      return (
        STATUS_PRE_MATRICULA[preMatricula.status] || { texto: preMatricula.status, cor: "grey" }
      );
    },
    async listar() {
      this.carregando = true;
      try {
        const resposta = await this.$services.preMatriculasService.listar({
          busca: this.filtros.busca || undefined,
          periodo_id: this.filtros.periodo_id || undefined,
          status: this.filtros.status || undefined,
          page: this.opcoes.page,
          por_pagina: this.opcoes.itemsPerPage,
        });
        this.preMatriculas = resposta.data;
        this.total = resposta.total;
      } catch (error) {
        this.$handleError(error);
      }
      this.carregando = false;
    },
    temBoleto(preMatricula) {
      return Boolean(preMatricula.parcela && preMatricula.parcela.linhaDigitavel);
    },
    /** Ações da linha, na ordem do fluxo: gerar, baixar, pagar, histórico, efetivar. */
    acoes(item) {
      const pendente = item.status === "BOLETO_PENDENTE";
      const matriculado = item.status === "MATRICULADO";
      const motivoSemPermissao = "Sem permissão para esta ação";
      // Motivo pelo status, para o tooltip dizer por que o botão está desabilitado.
      const jaPago = matriculado ? "Matrícula já efetivada" : "Pagamento já reconhecido";
      return [
        {
          icone: "fa fa-barcode",
          cor: "blue-grey lighten-3",
          titulo: "Gerar boleto",
          habilitada:
            this.podeEfetivar && this.podeGerarBoleto(item) && this.gerandoBoleto === null,
          motivo: (() => {
            if (!this.podeEfetivar) return motivoSemPermissao;
            if (!pendente) return `${jaPago}: não há boleto a gerar`;
            return "Boleto já gerado";
          })(),
          carregando: this.gerandoBoleto === item.id,
          executar: () => this.gerarBoleto(item),
        },
        {
          icone: "fa fa-download",
          cor: "blue-grey lighten-3",
          titulo: "Download do boleto",
          habilitada: this.temBoleto(item),
          motivo: pendente
            ? "Boleto ainda não gerado"
            : "Pago sem boleto: não há boleto para baixar",
          carregando: this.baixandoBoleto === item.id,
          executar: () => this.baixarBoleto(item),
        },
        {
          icone: "fa fa-money-bill",
          cor: "blue-grey lighten-3",
          titulo: "Registrar pagamento",
          habilitada: this.podeRegistrarPagamento && pendente,
          motivo: !this.podeRegistrarPagamento ? motivoSemPermissao : jaPago,
          carregando: false,
          executar: () => {
            this.preMatriculaEmPagamento = item.id;
          },
        },
        {
          icone: "fa fa-history",
          cor: "blue-grey lighten-3",
          titulo: "Histórico de pagamentos",
          habilitada: Boolean(item.parcela),
          motivo: "Sem parcela",
          carregando: false,
          executar: () => {
            this.preMatriculaNoHistorico = item;
          },
        },
        {
          icone: "fa fa-user-check",
          cor: "success",
          titulo: "Efetivar matrícula",
          habilitada: this.podeEfetivar && item.status === "BOLETO_PAGO",
          motivo: (() => {
            if (!this.podeEfetivar) return motivoSemPermissao;
            if (matriculado) return "Matrícula já efetivada";
            return "Efetivar exige boleto pago";
          })(),
          carregando: false,
          executar: () => {
            this.preMatriculaEmEfetivacao = item.id;
          },
        },
      ];
    },
    podeGerarBoleto(preMatricula) {
      return preMatricula.status === "BOLETO_PENDENTE" && !this.temBoleto(preMatricula);
    },
    async gerarBoleto(preMatricula) {
      this.gerandoBoleto = preMatricula.id;
      try {
        await this.$services.preMatriculasService.gerarBoleto(preMatricula.id);
        this.$toast.success("Boleto gerado.");
        await this.listar();
      } catch (error) {
        this.$toast.danger(mensagemDeErro(error));
      }
      this.gerandoBoleto = null;
    },
    async baixarBoleto(preMatricula) {
      this.baixandoBoleto = preMatricula.id;
      try {
        await this.$services.preMatriculasService.baixarBoleto(preMatricula.id);
      } catch (error) {
        this.$toast.danger("Não foi possível baixar o boleto. Tente novamente.");
      }
      this.baixandoBoleto = null;
    },
    async aoRegistrarPagamento() {
      this.preMatriculaEmPagamento = null;
      await this.listar();
    },
    async aoEfetivar(resposta) {
      this.preMatriculaEmEfetivacao = null;
      this.$toast.success("Matrícula efetivada.");
      this.matriculaEfetivada = resposta.matricula;
      await this.listar();
    },
  },
};
</script>
